/**
 * settings-modules.ts — Danh sách mục của Cài đặt hệ thống (/settings?m=<id>), CHỈ dữ liệu (không JSX).
 * Dùng chung cho trang Cài đặt (settings-page.tsx gắn phần vẽ) và bảng lệnh ⌘K — sửa nhãn / quyền một chỗ.
 */
import {
  BellRing, Bot, Building2, CircleDot, Database, HardDrive, History, KeyRound, ListChecks, LogIn, MessagesSquare,
  ShieldCheck, Tag, Workflow,
  type LucideIcon,
} from 'lucide-react'
import type { Role } from '@/types/api'
import { i18n } from '@/lib/i18n'

export interface SettingsModuleMeta {
  id: string
  label: string
  icon: LucideIcon
  description: string
  /** Bỏ trống = mọi vai trò. Bám đúng chỗ backend trả 403. */
  roles?: Role[]
  /** Quyền xem cần có (khoá `/me/permissions`) — khớp backend. Bỏ trống = không đòi quyền riêng. */
  perm?: string
  /** Từ khoá thêm cho bảng lệnh ⌘K (vd tên cũ của mục đã gộp). */
  keywords?: string[]
}

/** `title`/`label`/`description` là getter đọc `i18n.t()` lúc dùng — KHÔNG spread `{...item}` ở cấp module khi
 *  tiêu thụ mảng này (đóng băng giá trị, xem bẫy đã gặp ở `pages/account/account-page.tsx`). */
export const SETTINGS_GROUPS: { title: string; items: SettingsModuleMeta[] }[] = [
  {
    get title() { return i18n.t('settings:groups.org.title') },
    items: [
      {
        id: 'company', icon: Building2,
        get label() { return i18n.t('settings:modules.company.label') },
        get description() { return i18n.t('settings:modules.company.description') },
      },
      {
        id: 'tags', icon: Tag,
        get label() { return i18n.t('settings:modules.tags.label') },
        get description() { return i18n.t('settings:modules.tags.description') },
      },
      {
        id: 'media', icon: HardDrive,
        get label() { return i18n.t('settings:modules.media.label') },
        get description() { return i18n.t('settings:modules.media.description') },
        roles: ['owner', 'admin'],
      },
      {
        id: 'api-keys', icon: KeyRound,
        get label() { return i18n.t('settings:modules.apiKeys.label') },
        get description() { return i18n.t('settings:modules.apiKeys.description') },
        // Backend: api_keys.view / create / delete (Cài đặt → Vai trò & quyền); khoá mang đúng quyền của người tạo.
        perm: 'api_keys.view',
      },
      {
        // MCP connector (docs/THIET-KE-MCP-CONNECTOR.md). Không đòi quyền: ai cũng xem / thu hồi kết nối của mình;
        // công tắc bật/tắt chỉ hiện khi backend trả canManage (ai_connector.update), bảng cả tổ chức khi canViewAll (ai_connector.view).
        id: 'ai-connector', icon: Bot,
        get label() { return i18n.t('settings:modules.aiConnector.label') },
        get description() { return i18n.t('settings:modules.aiConnector.description') },
        keywords: ['claude', 'mcp', 'connector', 'anthropic', 'ai'],
      },
    ],
  },
  {
    // Phần CDP tách khỏi trang "Phân khúc & CDP" cũ (phân khúc nay là tab của Khách hàng). Backend: /cdp/* cần cdp.view / cdp.manage.
    get title() { return i18n.t('settings:groups.customerData.title') },
    items: [
      {
        // Gộp "Vòng đời khách hàng" cũ (`?m=customer-lifecycle` → mục này). Phần đặt giai đoạn chỉ chủ tổ chức / quản trị viên thấy.
        id: 'customer-stages', icon: Workflow,
        get label() { return i18n.t('settings:modules.customerStages.label') },
        get description() { return i18n.t('settings:modules.customerStages.description') },
        roles: ['owner', 'admin', 'manager'],
        keywords: ['vòng đời khách hàng', 'phễu', 'giai đoạn'], // i18n-ignore — từ khoá tìm ⌘K, không phải chữ hiển thị
      },
      {
        id: 'customer-properties', icon: ListChecks,
        get label() { return i18n.t('settings:modules.customerProperties.label') },
        get description() { return i18n.t('settings:modules.customerProperties.description') },
        roles: ['owner', 'admin', 'manager'],
      },
      {
        id: 'customer-events', icon: Database,
        get label() { return i18n.t('settings:modules.customerEvents.label') },
        get description() { return i18n.t('settings:modules.customerEvents.description') },
        roles: ['owner', 'admin', 'manager'],
      },
    ],
  },
  {
    // Mẫu SaleSmartly "Cài đặt hội thoại" (tự động phân bổ). Backend: PUT cần settings.update.
    get title() { return i18n.t('settings:groups.messaging.title') },
    items: [
      {
        id: 'conversation-settings', icon: MessagesSquare,
        get label() { return i18n.t('settings:modules.conversationSettings.label') },
        get description() { return i18n.t('settings:modules.conversationSettings.description') },
        roles: ['owner', 'admin'],
      },
      {
        // Mẫu SaleSmartly "Thông báo tin nhắn". Backend: PUT /notification-settings cần settings.update.
        id: 'message-notify', icon: BellRing,
        get label() { return i18n.t('settings:modules.messageNotify.label') },
        get description() { return i18n.t('settings:modules.messageNotify.description') },
        roles: ['owner', 'admin'],
      },
    ],
  },
  {
    // Mẫu SaleSmartly "Nhật ký kiểm toán". Backend: /audit/activity chỉ owner/admin;
    // /audit/logins ai cũng gọi được nhưng người thường chỉ thấy của mình (xem ở /account).
    get title() { return i18n.t('settings:groups.audit.title') },
    items: [
      {
        id: 'audit-activity', icon: History,
        get label() { return i18n.t('settings:modules.auditActivity.label') },
        get description() { return i18n.t('settings:modules.auditActivity.description') },
        roles: ['owner', 'admin'],
      },
      {
        id: 'audit-logins', icon: LogIn,
        get label() { return i18n.t('settings:modules.auditLogins.label') },
        get description() { return i18n.t('settings:modules.auditLogins.description') },
        roles: ['owner', 'admin'],
      },
      {
        id: 'audit-presence', icon: CircleDot,
        get label() { return i18n.t('settings:modules.auditPresence.label') },
        get description() { return i18n.t('settings:modules.auditPresence.description') },
        roles: ['owner', 'admin'],
      },
    ],
  },
  {
    // DSAR (Luật Bảo vệ dữ liệu cá nhân 91/2025/QH15 + NĐ 356/2025/NĐ-CP). Backend: /dsar-requests/* cần dsar.view/create/update/export;
    // nút "Xoá vĩnh viễn" riêng đòi contacts.delete_permanently (mặc định chỉ owner/admin).
    get title() { return i18n.t('settings:groups.legal.title') },
    items: [
      {
        id: 'dsar', icon: ShieldCheck,
        get label() { return i18n.t('settings:modules.dsar.label') },
        get description() { return i18n.t('settings:modules.dsar.description') },
        perm: 'dsar.view',
        keywords: ['dsar', 'quyền chủ thể dữ liệu', 'bảo vệ dữ liệu cá nhân', 'xoá vĩnh viễn'], // i18n-ignore — từ khoá tìm ⌘K
      },
    ],
  },
]

/**
 * Nhóm + mục vai trò được xem (nhóm trống thì bỏ). Mục có `perm` chỉ hiện khi đã tải quyền và có quyền xem đó
 * (`perms` chưa tải → ẩn mục đó, các mục khác vẫn lọc theo vai trò như navForRole).
 */
export function settingsGroupsForRole(role?: Role, perms?: Set<string>): { title: string; items: SettingsModuleMeta[] }[] {
  const allowed = (i: SettingsModuleMeta) =>
    // Mục chỉ đòi quyền (không có `roles` dự phòng) → ẩn tới khi tải xong quyền, không chớp hiện cho người không có quyền.
    (!i.roles || (!!role && i.roles.includes(role))) && (!i.perm || !!perms?.has(i.perm))
  return SETTINGS_GROUPS
    .map((g) => ({ ...g, items: g.items.filter(allowed) }))
    .filter((g) => g.items.length > 0)
}
